import { Box, IconButton, Stack, TextField, Tooltip, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/RefreshOutlined';
import { toDevanagariDigits, type MathCaptchaChallenge } from '../lib/captcha';
import { useLanguage } from '../lib/i18n';
import { PMC } from '../theme';

/** The "a op b = ?" widget shared by every write gated behind a math captcha. */
export function MathCaptchaField({
  challenge,
  isFetching,
  onRefresh,
  value,
  onChange,
  error,
  variant = 'default',
}: {
  challenge: MathCaptchaChallenge | undefined;
  isFetching: boolean;
  onRefresh: () => void;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  variant?: 'default' | 'card';
}) {
  const { t, lang } = useLanguage();
  const questionText = challenge ? (lang === 'mr' ? toDevanagariDigits(challenge.question) : challenge.question) : null;

  if (variant === 'card') {
    return (
      <Box>
        <Stack direction="row" spacing={1.5}>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                mb: 0.75,
                fontWeight: 700,
                color: '#64748b',
                letterSpacing: '.06em',
                fontSize: '0.7rem',
              }}
            >
              {t('search_card_security_check')}
            </Typography>
            <Box
              sx={{
                px: 1.5,
                py: 1.1,
                borderRadius: '12px',
                border: '1px dashed #bfdbfe',
                backgroundColor: '#eff6ff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                height: 44,
              }}
            >
              <Typography
                className="num"
                sx={{
                  fontWeight: 700,
                  fontSize: '1.05rem',
                  color: '#1e40af',
                  letterSpacing: '.05em',
                }}
              >
                {questionText ? `${questionText} =` : '…'}
              </Typography>
              <Tooltip title={t('captcha_refresh')}>
                <span>
                  <IconButton onClick={onRefresh} disabled={isFetching} size="small" sx={{ p: 0.5, color: '#2563eb' }}>
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                mb: 0.75,
                fontWeight: 700,
                color: '#64748b',
                letterSpacing: '.06em',
                fontSize: '0.7rem',
              }}
            >
              {t('search_card_your_answer')}
            </Typography>
            <TextField
              fullWidth
              placeholder={t('search_card_answer_ph')}
              value={value}
              onChange={(e) => onChange(e.target.value.replace(/[^0-9०-९]/g, '').slice(0, 4))}
              error={Boolean(error)}
              slotProps={{
                input: {
                  sx: {
                    borderRadius: '12px',
                    backgroundColor: '#f8fafc',
                    height: 44,
                    fontSize: '0.9rem',
                    '& fieldset': { borderColor: '#e2e8f0' },
                  },
                },
                htmlInput: { inputMode: 'numeric', autoComplete: 'off', maxLength: 4 },
              }}
            />
          </Box>
        </Stack>
        {error ? (
          <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.75, fontWeight: 500 }}>
            {error}
          </Typography>
        ) : null}
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
        {t('captcha_title')}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        {t('captcha_prompt')}
      </Typography>
      <Stack direction="row" spacing={1.25} alignItems="center">
        <Box
          className="num"
          sx={{
            px: 1.5,
            py: 1,
            borderRadius: 1.5,
            border: `1px solid ${PMC.line}`,
            backgroundColor: PMC.bgSoft,
            fontWeight: 700,
            minWidth: 76,
            textAlign: 'center',
          }}
        >
          {questionText ? `${questionText} =` : '…'}
        </Box>
        <TextField
          size="small"
          sx={{ width: 100 }}
          label={t('captcha_answer')}
          value={value}
          onChange={(e) => onChange(e.target.value.replace(/[^0-9०-९]/g, '').slice(0, 4))}
          error={Boolean(error)}
          // A CAPTCHA answer must never be browser-autofilled — that lets a
          // saved value from an earlier, different challenge silently fill
          // in without the user (or a bot) ever reading the actual question,
          // defeating the point of the challenge entirely.
          slotProps={{ htmlInput: { inputMode: 'numeric', autoComplete: 'off', maxLength: 4 } }}
        />
        <Tooltip title={t('captcha_refresh')}>
          <span>
            <IconButton onClick={onRefresh} disabled={isFetching}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
      {error ? (
        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
          {error}
        </Typography>
      ) : null}
    </Box>
  );
}
